import { Avatar } from 'module/ui/components/atoms/avatar';
import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { DraggableTable } from 'module/ui/components/molecules/draggable-table';
import { TableBody } from 'module/ui/components/molecules/draggable-table/styles';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import { faTrash, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from '@rocketlane/rocket-router';
import { PageTemplateLite } from 'api/page-template/types';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';

const StyledDraggableTable = styled.div`
  ${TableBody} {
    max-height: calc(85vh - 100px);
    overflow-y: auto;
  }
`;

const LoadMoreFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: var(--spacing-04) 0;
  min-height: 48px;
`;

const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
  pointer-events: none;
`;

export const CompanyTemplateTable: React.FC<{
  templates: PageTemplateLite[];
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onReorder?: (
    templates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => void;
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}> = ({
  templates,
  onEdit,
  onDelete,
  onReorder,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
}) => {
  const navigate = useNavigate();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || !onLoadMore) {
      return;
    }

    const root = tableWrapperRef.current?.querySelector<HTMLElement>(
      '[data-draggable-table-body="true"]',
    );
    const sentinel = loadMoreSentinelRef.current;

    if (!root || !sentinel) {
      return;
    }

    const LOAD_MORE_THRESHOLD_PX = 48;

    const isNearBottom = () => {
      const remaining = root.scrollHeight - root.scrollTop - root.clientHeight;

      return remaining <= LOAD_MORE_THRESHOLD_PX;
    };

    const tryLoadMore = () => {
      if (loadingMore || !isNearBottom()) {
        return;
      }
      onLoadMore();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          tryLoadMore();
        }
      },
      { root, rootMargin: '0px', threshold: 0 },
    );

    observer.observe(sentinel);
    root.addEventListener('scroll', tryLoadMore, { passive: true });

    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', tryLoadMore);
    };
  }, [hasMore, loadingMore, onLoadMore, templates.length]);

  const actionMenuChildren = [
    ...(onEdit
      ? [
          {
            key: 'edit',
            label: <FormattedMessage id="edit" />,
            icon: <FontAwesomeIcon icon={faEdit} />,
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            key: 'delete',
            label: <FormattedMessage id="delete" />,
            icon: <FontAwesomeIcon icon={faTrash} />,
            danger: true,
          },
        ]
      : []),
  ];
  const columns: ColumnType<PageTemplateLite>[] = [
    {
      key: 'templateName',
      title: <FormattedMessage id="template_name" />,
      render: (value, _record, _index, _data) => {
        return (
          <FlexWrapperWithShortGap>
            <div>{value}</div>
          </FlexWrapperWithShortGap>
        );
      },
    },
    {
      key: 'lastUpdatedBy',
      title: <FormattedMessage id="last_updated_by" />,
      render: (user) =>
        user ? (
          <>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            {user.firstName} {user.lastName}
          </>
        ) : null,
    },
    {
      key: 'updatedAt',
      title: <FormattedMessage id="last_updated_on" />,
      render: (date) => <HumanisedDate timestamp={moment(date)} />,
    },
    {
      key: 'createdBy',
      title: <FormattedMessage id="created_by" />,
      render: (user) =>
        user ? (
          <>
            <Avatar
              name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
              size={20}
              src={user.company?.companyLogoUrl}
            />
            {user.firstName} {user.lastName}
          </>
        ) : null,
    },
    {
      key: 'createdAt',
      title: <FormattedMessage id="created_on" />,
      render: (date) => <HumanisedDate timestamp={moment(date)} />,
    },
    ...(actionMenuChildren.length > 0
      ? [
          {
            key: 'actions',
            width: '56px',
            title: '',
            render: (_: unknown, record: PageTemplateLite) => (
              <MoreActionsDropdown
                menu={{
                  onClick: ({ key }) => {
                    if (key === 'edit' && onEdit) {
                      onEdit(String(record.templateId));
                    } else if (key === 'delete' && onDelete) {
                      onDelete(String(record.templateId));
                    }
                  },
                  items: [
                    {
                      type: 'group',
                      label: <FormattedMessage id="more_actions" />,
                      children: actionMenuChildren,
                    },
                  ],
                }}
              />
            ),
          },
        ]
      : []),
  ];

  const bodyFooter =
    hasMore || loadingMore ? (
      <>
        {hasMore ? (
          <LoadMoreSentinel
            ref={loadMoreSentinelRef}
            data-cy="company-template-table-load-more-sentinel"
            aria-hidden
          />
        ) : null}
        {loadingMore ? (
          <LoadMoreFooter data-cy="next-page-list-loader">
            <Loading size="small" />
          </LoadMoreFooter>
        ) : null}
      </>
    ) : null;

  return (
    <StyledDraggableTable ref={tableWrapperRef}>
      <DraggableTable<PageTemplateLite & { id: string }>
        columns={columns}
        data={templates.map((template) => ({
          ...template,
          id: String(template.templateId),
        }))}
        onDragEnd={
          onReorder &&
          ((oldIndex, newIndex) => {
            const newTemplates = [...templates];
            const [movedItem] = newTemplates.splice(oldIndex, 1);

            newTemplates.splice(newIndex, 0, movedItem);
            onReorder(newTemplates, movedItem.templateId, newIndex);
          })
        }
        onRowClick={(record) => {
          navigate(`${record?.templateId}`);
        }}
        loading={loading}
        bodyFooter={bodyFooter}
      />
    </StyledDraggableTable>
  );
};
